import { Box, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import TableComponent from 'components/Table';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import MobileKduesList from './mobile-kdues-list';
import { columnsDataSet, generateKDueAdjustments, generatekDues, generatePartiesCollapseRow } from './services';

const DESKTOP_ROW_HEIGHT = 62;

const useStyles = makeStyles((theme) => ({
  scrollWrapper: {
    overflowX: 'auto',
    width: '100%',
    '&&::-webkit-scrollbar': { height: theme.spacing(1) },
    scrollbarColor: `${theme.palette.secondary.main} ${theme.palette.background.paper}`,
    scrollbarWidth: 'thin',
    '&&::-webkit-scrollbar-track': { borderRadius: theme.spacing(0.5) },
    '&&::-webkit-scrollbar-thumb': {
      background: theme.palette.secondary.main,
      borderRadius: 4,
    },
    '&&::-webkit-scrollbar-thumb:hover': { background: theme.palette.secondary.dark },
    '& thead tr': {
      height: DESKTOP_ROW_HEIGHT,
    },
    '& thead th, & thead td': {
      height: DESKTOP_ROW_HEIGHT,
      paddingTop: 0,
      paddingBottom: 0,
      verticalAlign: 'middle',
    },
  },
}));

type TableProtokduestype = {
	type: 'adjustments' | 'dues' | 'addendums',
	title: string,
	handleEditModal:(item:columnsDataSet)=> void,
	handleChangePayees?: (item:columnsDataSet)=> void,
	handleChangePayers?: (item:columnsDataSet)=> void,
	itemsCollapsed?: { [key: string]: boolean },
	onToggleCollapse?: (item:columnsDataSet)=> void,
	handleDelete: (item:columnsDataSet)=> void,
	data: any
}

const TableProtoKdues: FC<TableProtokduestype> = ({
  type,
  title,
  handleEditModal,
  handleDelete,
  handleChangePayers,
  handleChangePayees,
  itemsCollapsed,
  onToggleCollapse,
  data
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const classes = useStyles();
  const isCompact = !useMediaQuery((mediaTheme: any) => mediaTheme.breakpoints.up('lg'));

  const columns = useMemo(() => {
    switch (type) {
      case 'dues':
        return generatekDues(
          t,
          theme,
          title,
          handleEditModal,
          handleDelete,
          handleChangePayees,
          handleChangePayers,
          itemsCollapsed,
          onToggleCollapse
        );
	  case 'adjustments':
        return generateKDueAdjustments(
          t,
          theme,
          title,
          handleEditModal,
          handleDelete,
          undefined,
          handleChangePayees,
          handleChangePayers,
          itemsCollapsed,
          onToggleCollapse
		  );
		  case 'addendums':
        return generateKDueAdjustments(
          t,
          theme,
          title,
          handleEditModal,
          handleDelete,
		  true,
          handleChangePayees,
          handleChangePayers,
          itemsCollapsed,
          onToggleCollapse
			  );
      default: return generatekDues(
        t,
        theme,
        title,
        handleEditModal,
        handleDelete,
        handleChangePayees,
        handleChangePayers,
        itemsCollapsed,
        onToggleCollapse
		  );
    }
  }, [handleChangePayees, handleChangePayers, handleDelete, handleEditModal, itemsCollapsed, onToggleCollapse, t, theme, title, type]);

  const collapsedColumns = useMemo(
    () => (onToggleCollapse ? generatePartiesCollapseRow(t, handleChangePayees, handleChangePayers) : []),
    [handleChangePayees, handleChangePayers, onToggleCollapse, t]
  );

  if (isCompact) {
    return (
      <MobileKduesList
        type={type}
        title={title}
        data={data}
        handleEditModal={handleEditModal}
        handleDelete={handleDelete}
        handleChangePayees={handleChangePayees}
        handleChangePayers={handleChangePayers}
        itemsCollapsed={itemsCollapsed}
        onToggleCollapse={onToggleCollapse}
      />
    );
  }

  return (
    <Box className={classes.scrollWrapper}>
      <TableComponent
        disabledBorderLeft
        columns={columns}
        dataSet={data}
        columnCollapsing={collapsedColumns}
        itemsCollapsed={itemsCollapsed}
        subHeaderFirst={type !== 'dues'}
      />
    </Box>
  );
};

export default TableProtoKdues;
